import type {
  ActivityId,
  AreaId,
  GyymApiClient,
  PrefectureDetail,
  PrefectureId,
  SearchMetadata,
} from '@gyym/sdk'
import {
  cachedGyymPrefetch,
  gyymPrefetch,
  PrefetchReturn,
} from '@gyym/sdk-next'

import {
  ActivitySlug,
  activitySlugExists,
  getActivityIdBySlug,
} from '@/modules/Activities'
import {
  getPrefectureBySlug,
  PrefectureSlug,
  prefectureSlugExists,
} from '@/modules/Prefectures'
import { isNumeric } from '@/utils/isNumeric'

export enum SegmentType {
  PREFECTURE = 'prefecture',
  ACTIVITY = 'activity',
  AREA_ID = 'areaId',
  CITY_CODE = 'cityCode',
  TAG = 'tag',
}

type SegmentValue<T extends SegmentType> = T extends SegmentType.PREFECTURE
  ? PrefectureId
  : T extends SegmentType.ACTIVITY
  ? ActivityId
  : T extends SegmentType.AREA_ID
  ? AreaId
  : T extends SegmentType.CITY_CODE
  ? string
  : T extends SegmentType.TAG
  ? string
  : never

type PathSegments = [] | [string] | [string, string] | [string, string, string]

type SegmentResource<T extends SegmentType = SegmentType> = {
  type: T | null
  value: SegmentValue<T> | null
  cache: PrefetchReturn
}

type SegmentResourceResult =
  | {
      props: {
        segmentTypes:
          | []
          | [SegmentType]
          | [SegmentType, SegmentType]
          | [SegmentType, SegmentType, SegmentType]
      }
      cache: PrefetchReturn
      notFound?: never
    }
  | { notFound: true }

/**
 * Area id prefix
 */
export const AREA_ID_PREFIX = 'A' as const

/**
 * City code prefix
 */
export const CITY_CODE_PREFIX = 'C' as const

/**
 * Check if the value is a valid area id
 *
 * @param value Value to be checked
 * @returns boolean
 */
const isAreaId = (value: string): boolean =>
  value.startsWith(AREA_ID_PREFIX) && isNumeric(value.substring(1))

/**
 * Check if the value is a valid city code
 *
 * @param value Value to be checked
 * @returns
 */
const isCityCode = (value: string): boolean => {
  if (!value.startsWith(CITY_CODE_PREFIX)) return false
  const cityCode = value.substring(1)
  // City code in URLs is always 5 digits (leading 0 included)
  return /^\d{5}$/.test(cityCode)
}

/**
 * Convert path to segment type enum
 *
 * @param value Segment value
 * @returns SegmentTYpe
 * @throws Error
 */
export const getSegmentType = (value: string): SegmentType | never => {
  if (prefectureSlugExists(value as PrefectureSlug)) {
    return SegmentType.PREFECTURE
  }

  if (activitySlugExists(value as ActivitySlug)) {
    return SegmentType.ACTIVITY
  }

  if (isAreaId(value)) {
    return SegmentType.AREA_ID
  }

  if (isCityCode(value)) {
    return SegmentType.CITY_CODE
  }

  if (value) {
    return SegmentType.TAG
  }

  throw new Error(`Invalid segment value: ${value}`)
}

/**
 * Cast segment value to the corresponding type
 *
 * @param type SegmentType
 * @param value Segment value
 * @returns SegmentValue<T>
 */
export const castSegmentValue = <T extends SegmentType>(
  type: T,
  value: string,
): SegmentValue<T> => {
  switch (type) {
    case SegmentType.PREFECTURE:
      return getPrefectureBySlug(value as PrefectureSlug)!.id as SegmentValue<T>
    case SegmentType.ACTIVITY:
      return getActivityIdBySlug(value as ActivitySlug) as SegmentValue<T>
    case SegmentType.AREA_ID:
      return Number(value.substring(1)) as SegmentValue<T>
    case SegmentType.CITY_CODE:
      // The API validates city code as an integer and rejects a leading 0
      return `${Number(value.substring(1))}` as SegmentValue<T>
    case SegmentType.TAG:
      return value as SegmentValue<T>
    default:
      return value as SegmentValue<T>
  }
}

const fetchPrefectureResource = async (
  segment: string,
): Promise<SegmentResource | false> => {
  return {
    type: SegmentType.PREFECTURE,
    value: castSegmentValue(SegmentType.PREFECTURE, segment),
    cache: [],
  }
}

const fetchActivityResource = async (
  segment: string,
  client: GyymApiClient,
  metaActivities: SearchMetadata['activities'],
): Promise<SegmentResource | false> => {
  const activityId = castSegmentValue(SegmentType.ACTIVITY, segment)

  if (!activityId) {
    return false
  }

  const hasMetaActivity = metaActivities.some(({ id }) => id === activityId)

  return {
    type: SegmentType.ACTIVITY,
    value: activityId,
    cache: hasMetaActivity
      ? []
      : [gyymPrefetch(client.user.activities.get, activityId)],
  }
}

const fetchAreaResource = async (
  segment: string,
  client: GyymApiClient,
  metaPrefectures: SearchMetadata['prefectures'],
): Promise<SegmentResource | false> => {
  const areaId = castSegmentValue(SegmentType.AREA_ID, segment)

  const hasMetaArea = metaPrefectures.some(({ areas }) => {
    return areas.some(({ id }) => id === areaId)
  })

  return {
    type: SegmentType.AREA_ID,
    value: areaId,
    cache: hasMetaArea ? [] : [gyymPrefetch(client.user.areas.get, areaId)],
  }
}

const fetchCityCodeResource = async (
  segment: string,
  client: GyymApiClient,
): Promise<SegmentResource | false> => {
  const cityCode = castSegmentValue(SegmentType.CITY_CODE, segment)

  return {
    type: SegmentType.CITY_CODE,
    value: cityCode,
    cache: [gyymPrefetch(client.user.facilities.getCityNameByCode, cityCode)],
  }
}

const fetchTagResource = async (
  segment: string,
  _client: GyymApiClient,
): Promise<SegmentResource | false> => {
  const tag = castSegmentValue(SegmentType.TAG, segment)

  return {
    type: SegmentType.TAG,
    value: tag,
    cache: [
      // Handle cache on /facilities/tags/[tag] page
    ],
  }
}

const fetchSegmentResource = async (
  segment: string,
  client: GyymApiClient,
  meta: SearchMetadata,
): Promise<SegmentResource | false> => {
  const segmentType = getSegmentType(segment)

  switch (segmentType) {
    case SegmentType.PREFECTURE:
      return fetchPrefectureResource(segment)
    case SegmentType.ACTIVITY:
      return fetchActivityResource(segment, client, meta.activities)
    case SegmentType.AREA_ID:
      return fetchAreaResource(segment, client, meta.prefectures)
    case SegmentType.CITY_CODE:
      return fetchCityCodeResource(segment, client)
    case SegmentType.TAG:
      return fetchTagResource(segment, client)
    default:
      return false
  }
}

const validateSegments = (
  segmentResources: SegmentResource[],
  prefectures: PrefectureDetail[],
): boolean => {
  const [first, second, third] = segmentResources

  // Segment should not be more than 3
  if (segmentResources.length > 3) return false

  // Segment could be empty (nothing else to validate)
  if (segmentResources.length === 0) return true

  if (segmentResources.length === 1) {
    // First segment must be prefecture, activity or a tag
    return (
      first?.type === SegmentType.PREFECTURE ||
      first?.type === SegmentType.ACTIVITY ||
      first?.type === SegmentType.TAG
    )
  }

  // If first segment is activity, second segment should not exist
  if (first?.type === SegmentType.ACTIVITY) {
    return false
  }

  if (first?.type === SegmentType.PREFECTURE) {
    // If first segment is prefecture, second segment should be area, cityCode, or activity
    const isSegmentsValid =
      second?.type === SegmentType.AREA_ID ||
      second?.type === SegmentType.CITY_CODE ||
      second?.type === SegmentType.ACTIVITY

    if (!isSegmentsValid) return false

    if (second.type === SegmentType.ACTIVITY) {
      // If second segment is activity, we have nothing else to validate
      return true
    }

    // Find the target prefecture
    const prefecture = prefectures.find(
      ({ id }) => id === first.value,
    ) as PrefectureDetail

    // If the second segment is area, we should check if the area is contained in the prefecture
    if (second.type === SegmentType.AREA_ID) {
      return prefecture.areas.some(({ id }) => id === second.value)
    }

    // If the second segment is cityCode, we should check if the cityCode is contained in the prefecture
    if (second.type === SegmentType.CITY_CODE) {
      return prefecture.cities.some(({ code }) => code === Number(second.value))
    }
  }

  if (segmentResources.length === 3) {
    // If there are 3 segments, the first segment must be prefecture, the second segment must be area or cityCode, and the third segment must be activity
    return (
      first?.type === SegmentType.PREFECTURE &&
      (second?.type === SegmentType.AREA_ID ||
        second?.type === SegmentType.CITY_CODE) &&
      third?.type === SegmentType.ACTIVITY
    )
  }

  return false
}

/**
 * Fetch resources for each segment
 *
 * @param segments Segment value array
 * @param client GyymClient
 * @returns
 */
export const fetchResources = async (
  [firstSegment, secondSegment, thirdSegment]: PathSegments,
  client: GyymApiClient,
): Promise<SegmentResourceResult> => {
  // ユーザー非依存のカタログのため、サーバー内共有キャッシュから取得する
  const [[metaKey, meta], [prefecturesKey, prefectures]] = await Promise.all([
    cachedGyymPrefetch((c) => c.user.facilities.searchMetadata),
    cachedGyymPrefetch((c) => c.user.prefectures.list),
  ])

  const firstResource = firstSegment
    ? await fetchSegmentResource(firstSegment, client, meta)
    : false

  const secondResource = secondSegment
    ? await fetchSegmentResource(secondSegment, client, meta)
    : false

  const thirdResource = thirdSegment
    ? await fetchSegmentResource(thirdSegment, client, meta)
    : false

  // Return 404 if any segment is specified but resource not found
  if (
    (firstSegment && !firstResource) ||
    (secondSegment && !secondResource) ||
    (thirdSegment && !thirdResource)
  ) {
    return {
      notFound: true,
    }
  }

  // Validate segments
  const isValid = validateSegments(
    [firstResource, secondResource, thirdResource].filter(
      Boolean,
    ) as SegmentResource[],
    prefectures,
  )

  if (!isValid) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      segmentTypes: [
        ...(firstResource ? [firstResource.type] : []),
        ...(secondResource ? [secondResource.type] : []),
        ...(thirdResource ? [thirdResource.type] : []),
      ] as
        | []
        | [SegmentType]
        | [SegmentType, SegmentType]
        | [SegmentType, SegmentType, SegmentType],
    },
    cache: [
      [metaKey, meta],
      [prefecturesKey, prefectures],
      ...(firstResource ? firstResource.cache : []),
      ...(secondResource ? secondResource.cache : []),
      ...(thirdResource ? thirdResource.cache : []),
    ],
  }
}
